import { isDebugFlagEnabled } from './debugTools';
import {
  CARE_ACTIVITY_CONTEXT_PATTERN,
  CARE_ACTIVITY_PARTICIPATION_PATTERN,
  CARE_BEHAVIOR_DIRECT_EMOTION_PATTERN,
  CARE_BEHAVIOR_EMOTION_CONTEXT_PATTERN,
  CARE_BEHAVIOR_FOCUS_PATTERN,
  CARE_BEHAVIOR_INCIDENT_WITH_SLAP_PATTERN,
  CARE_BEHAVIOR_PROMPTING_PATTERN,
  CARE_BEHAVIOR_REFUSAL_PATTERN,
  CARE_BEHAVIOR_SUPPORT_PATTERN,
  CARE_FOOD_BITES_PATTERN,
  CARE_FOOD_CATEGORY_PATTERN,
  CARE_FOOD_CATEGORY_REFUSAL_PATTERN,
  CARE_FOOD_ITEM_PATTERN,
  CARE_FOOD_ITEM_REFUSAL_PATTERN,
  CARE_MEAL_CONTEXT_PATTERN,
  CARE_MEDICATION_ADMIN_PATTERN,
  CARE_MEDICATION_DOMAIN_BOUNDARY_PATTERN,
  CARE_MEDICATION_DOSE_MG_PATTERN,
  CARE_MEDICATION_NAME_PATTERN,
  CARE_MEDICATION_PHRASE_PATTERN,
  CARE_MEDICATION_TABLET_FORM_PATTERN,
  CARE_MILESTONE_SIGNAL_PATTERN,
  CARE_MOOD_CALM_PATTERN,
  CARE_MOOD_UPSET_PATTERN,
  CARE_SLEEP_CATEGORY_PATTERN,
  CARE_SUMMARY_PATTERNS,
} from '../shared/careSignalPatterns';

const CATEGORY_PRIORITY = ['behavior', 'medication', 'food', 'sleep', 'mood', 'activity', 'milestone'];

export const CATEGORY_LABELS = {
  behavior: 'Behavior',
  medication: 'Medication',
  food: 'Food',
  sleep: 'Sleep',
  mood: 'Mood',
  activity: 'Activity',
  milestone: 'Milestone',
  log: 'Daily Log',
  unknown: 'Uncategorized',
};

const CONTINUATION_PATTERNS = [
  /\b(?:and|but|then|while|after|before|because|so)\b/i,
  /\b(?:the rest of (?:her|his|their) meal|the rest of the meal|finished (?:her|his|their) juice|finished (?:her|his|their) snack|calmed down|settled|needed support|with support|returned to class)\b/i,
];

const CATEGORY_RULES = {
  behavior: [
    { label: 'incident action', weight: 6, pattern: CARE_BEHAVIOR_INCIDENT_WITH_SLAP_PATTERN },
    { label: 'behavior refusal', weight: 5, pattern: CARE_BEHAVIOR_REFUSAL_PATTERN },
    { label: 'behavior focus', weight: 5, pattern: CARE_BEHAVIOR_FOCUS_PATTERN },
    { label: 'behavior emotion + context', weight: 4, pattern: CARE_BEHAVIOR_EMOTION_CONTEXT_PATTERN },
    { label: 'behavior direct emotion', weight: 2, pattern: CARE_BEHAVIOR_DIRECT_EMOTION_PATTERN },
    { label: 'behavior crying', weight: 4, pattern: /\b(?:cries|cried|crying)\b/i },
    { label: 'behavior incident context', weight: 2, pattern: /\b(?:left the classroom|ran toward the hallway|ran away|left area without permission|disrupted|disruptive)\b/i },
    { label: 'behavior attention', weight: 4, pattern: /\b(?:distracted|off task|off-task)\b/i },
    { label: 'behavior prompting', weight: 4, pattern: CARE_BEHAVIOR_PROMPTING_PATTERN },
    { label: 'behavior support', weight: 3, pattern: CARE_BEHAVIOR_SUPPORT_PATTERN },
    { label: 'behavior sensory response', weight: 4, pattern: /\b(?:covered (?:his|her|their) ears|covered ears|sensory break|sensory breaks|overstimulat(?:ed|ion)|too noisy|noise(?:\s+)?was too much)\b/i },
  ],
  medication: [
    { label: 'medication name', weight: 6, pattern: CARE_MEDICATION_NAME_PATTERN },
    { label: 'dose amount', weight: 6, pattern: CARE_MEDICATION_DOSE_MG_PATTERN },
    { label: 'medication generic', weight: 4, pattern: /\b(?:medication|meds?|medicine|dose|capsule)\b/i },
    { label: 'medication tablet form', weight: 5, pattern: CARE_MEDICATION_TABLET_FORM_PATTERN },
    { label: 'medication admin', weight: 4, pattern: CARE_MEDICATION_ADMIN_PATTERN },
    { label: 'medication phrase', weight: 5, pattern: CARE_MEDICATION_PHRASE_PATTERN },
  ],
  food: [
    { label: 'meal context', weight: 4, pattern: CARE_MEAL_CONTEXT_PATTERN },
    { label: 'eating action', weight: 5, pattern: CARE_FOOD_CATEGORY_PATTERN },
    { label: 'food item', weight: 3, pattern: CARE_FOOD_ITEM_PATTERN },
    { label: 'food refusal', weight: 6, pattern: CARE_FOOD_CATEGORY_REFUSAL_PATTERN },
    { label: 'food item refusal', weight: 6, pattern: CARE_FOOD_ITEM_REFUSAL_PATTERN },
    { label: 'food bites', weight: 6, pattern: CARE_FOOD_BITES_PATTERN },
  ],
  sleep: [
    { label: 'sleep rest', weight: 5, pattern: CARE_SLEEP_CATEGORY_PATTERN },
  ],
  mood: [
    { label: 'mood calm', weight: 4, pattern: CARE_MOOD_CALM_PATTERN },
    { label: 'mood upset state', weight: 3, pattern: CARE_MOOD_UPSET_PATTERN },
  ],
  activity: [
    { label: 'activity participation', weight: 3, pattern: CARE_ACTIVITY_PARTICIPATION_PATTERN },
    { label: 'activity context', weight: 1, pattern: CARE_ACTIVITY_CONTEXT_PATTERN },
  ],
  milestone: [
    { label: 'milestone signal', weight: 4, pattern: CARE_MILESTONE_SIGNAL_PATTERN },
  ],
};

const CATEGORY_THRESHOLDS = {
  behavior: 4,
  medication: 4,
  food: 4,
  sleep: 3,
  mood: 3,
  activity: 3,
  milestone: 4,
};

const CATEGORY_ORDER = CATEGORY_PRIORITY.slice();

export const normalizeCategoryText = (value = '') => String(value || '')
  .toLowerCase()
  .replace(/https?:\/\/\S+/g, ' ')
  .replace(/[^a-z0-9]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

export const isSummaryText = (value = '') => {
  const normalized = normalizeCategoryText(value);
  if (!normalized) {
    return false;
  }

  return CARE_SUMMARY_PATTERNS.some((pattern) => pattern.test(normalized));
};

export const splitCategoryClauses = (inputText = '', {
  enableFoodBehaviorBoundarySplit = false,
} = {}) => {
  const raw = String(inputText || '').replace(/\r\n/g, '\n').trim();
  if (!raw) {
    return [];
  }

  const paragraphs = raw
    .split(/\n{2,}/)
    .map((block) => block
      .split('\n')
      .map((line) => String(line || '').trim())
      .filter(Boolean)
      .join(' '))
    .map((block) => block.replace(/\s+/g, ' ').trim())
    .filter(Boolean);

  const clauses = [];
  paragraphs.forEach((paragraph) => {
    if (paragraph) {
      clauses.push(
        ...paragraph
          .split(/(?<=[.!?])\s+(?=[A-Z0-9"“(])/u)
          .flatMap((part) => splitDomainBoundarySentence(part, { enableFoodBehaviorBoundarySplit }))
          .map((part) => part.trim())
          .filter(Boolean)
      );
    }
  });

  return clauses.length ? clauses : [raw];
};

const hasMatch = (pattern, text) => pattern.test(text);

const isContinuationText = (normalizedText = '') => CONTINUATION_PATTERNS.some((pattern) => pattern.test(normalizedText));

const hasFoodContinuationAnchor = (normalizedText = '') => {
  const text = String(normalizedText || '').trim();
  if (!text) {
    return false;
  }

  return /\b(?:ate|drank|eating|drinking|finished|few bites|a few bites|most of|appetite|hungry|meal|snack|breakfast|lunch|dinner|food|juice|milk|water|refused|would not eat|wouldn't eat|did not want|didn't want)\b/i.test(text)
    || /\b(?:the rest of (?:her|his|their) meal|the rest of the meal|more of (?:her|his|their) meal|more food|another bite|another few bites|still hungry|not hungry)\b/i.test(text);
};

const hasGenericSchoolCommunication = (normalizedText = '') => (
  /\b(?:school|teacher|class|classroom|aba|bcba|therap(?:y|ist)|ot|speech)\b.*\b(?:called|call|reported|said|emailed|messaged|texted|noted|told|feedback)\b/i.test(normalizedText)
  || /\b(?:called|call|reported|said|emailed|messaged|texted|noted|told|feedback)\b.*\b(?:school|teacher|class|classroom|aba|bcba|therap(?:y|ist)|ot|speech)\b/i.test(normalizedText)
);

const hasMealAsTimeContext = (normalizedText = '') => (
  /\b(?:around|at|near|before|after|during|by)\s+(?:breakfast|lunch|lunchtime|dinner|snack|meal(?:\s*time)?)\b/i.test(normalizedText)
  || /\b(?:breakfast|lunch|lunchtime|dinner|snack|meal(?:\s*time)?)\s+(?:time|period)\b/i.test(normalizedText)
);

const hasExplicitFoodRelation = (normalizedText = '') => (
  CARE_FOOD_CATEGORY_REFUSAL_PATTERN.test(normalizedText)
  || CARE_FOOD_ITEM_REFUSAL_PATTERN.test(normalizedText)
  || CARE_FOOD_BITES_PATTERN.test(normalizedText)
  || /\b(?:ate|eating|eaten|drank|drinking|finished|appetite|hungry|fed|feeding|would not eat|wouldn t eat|did not eat|didn t eat|skipped|refused)\b/i.test(normalizedText)
  || /\b(?:about|regarding|because of|due to|for|over)\s+(?:.*\b)?(?:breakfast|lunch|lunchtime|dinner|snack|meal|food|eating|drinking|appetite)\b/i.test(normalizedText)
);

const hasFoodOnlyAsSchoolCommunicationTimeContext = (normalizedText = '') => (
  hasGenericSchoolCommunication(normalizedText)
  && hasMealAsTimeContext(normalizedText)
  && !hasExplicitFoodRelation(normalizedText)
);

const scoreCategory = (normalizedText, category) => {
  const rules = CATEGORY_RULES[category] || [];
  const matchedSignals = [];
  let score = 0;

  rules.forEach((rule) => {
    if (hasMatch(rule.pattern, normalizedText)) {
      score += rule.weight;
      matchedSignals.push(rule.label);
    }
  });

  return {
    category,
    score,
    matchedSignals,
  };
};

const applyCrossCategoryPenalties = (scores, normalizedText) => {
  const nextScores = { ...scores };

  const behaviorScore = nextScores.behavior?.score || 0;
  const medicationScore = nextScores.medication?.score || 0;
  const foodScore = nextScores.food?.score || 0;
  const moodScore = nextScores.mood?.score || 0;

  if (behaviorScore >= (CATEGORY_THRESHOLDS.behavior || 4)) {
    nextScores.mood = {
      ...nextScores.mood,
      score: Math.max(0, moodScore - 3),
    };
    nextScores.activity = {
      ...nextScores.activity,
      score: Math.max(0, (nextScores.activity?.score || 0) - 3),
    };
  }

  if (medicationScore >= (CATEGORY_THRESHOLDS.medication || 4)) {
    nextScores.activity = {
      ...nextScores.activity,
      score: Math.max(0, (nextScores.activity?.score || 0) - 4),
    };
  }

  if (foodScore >= (CATEGORY_THRESHOLDS.food || 4)) {
    nextScores.activity = {
      ...nextScores.activity,
      score: Math.max(0, (nextScores.activity?.score || 0) - 3),
    };
  }

  if (hasFoodOnlyAsSchoolCommunicationTimeContext(normalizedText)) {
    nextScores.food = {
      ...nextScores.food,
      score: 0,
      matchedSignals: [],
    };
  }

  if (moodScore >= (CATEGORY_THRESHOLDS.mood || 3) && !behaviorScore) {
    nextScores.activity = {
      ...nextScores.activity,
      score: Math.max(0, (nextScores.activity?.score || 0) - 1),
    };
  }

  // Explicitly stop "activity" from stealing summary or vague sentences.
  if (isSummaryText(normalizedText)) {
    nextScores.activity = {
      ...nextScores.activity,
      score: 0,
    };
  }

  return nextScores;
};

const chooseCategoryFromScores = (scores) => {
  const ordered = CATEGORY_ORDER
    .map((category) => scores[category] || { category, score: 0, matchedSignals: [] })
    .sort((a, b) => {
      if (b.score !== a.score) {
        return b.score - a.score;
      }

      return CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category);
    });

  const [winner, runnerUp] = ordered;
  return { winner, runnerUp };
};

export const analyzeCategory = (inputText = '', {
  previousCategory = null,
  allowUnknown = true,
} = {}) => {
  const normalizedText = normalizeCategoryText(inputText);
  if (!normalizedText) {
    return {
      category: allowUnknown ? 'unknown' : 'log',
      confidence: 'none',
      normalizedText,
      scores: {},
      matchedSignals: [],
      runnerUp: null,
      reason: ['empty'],
    };
  }

  if (isSummaryText(normalizedText)) {
    return {
      category: allowUnknown ? 'unknown' : 'log',
      confidence: 'low',
      normalizedText,
      scores: {},
      matchedSignals: [],
      runnerUp: null,
      reason: ['summary'],
    };
  }

  const rawScores = CATEGORY_ORDER.reduce((acc, category) => {
    acc[category] = scoreCategory(normalizedText, category);
    return acc;
  }, {});

  const scores = applyCrossCategoryPenalties(rawScores, normalizedText);
  const { winner, runnerUp } = chooseCategoryFromScores(scores);
  const threshold = CATEGORY_THRESHOLDS[winner?.category] || Number.MAX_SAFE_INTEGER;
  const topScore = winner?.score || 0;
  const runnerUpScore = runnerUp?.score || 0;
  const confidence =
    topScore >= threshold + 3 ? 'high' :
      topScore >= threshold ? 'medium' :
        topScore > 0 ? 'low' : 'none';

  const reasons = [
    ...(winner?.matchedSignals || []).map((signal) => `${winner.category}:${signal}`),
  ];
  if (runnerUp?.category && runnerUpScore > 0) {
    reasons.push(`runner_up:${runnerUp.category}`);
  }

  if (topScore < threshold) {
    if (previousCategory && normalizedText && isContinuationText(normalizedText)) {
      if (previousCategory === 'food' && !hasFoodContinuationAnchor(normalizedText)) {
        return {
          category: allowUnknown ? 'unknown' : 'log',
          confidence,
          normalizedText,
          scores,
          matchedSignals: winner?.matchedSignals || [],
          runnerUp,
          reason: reasons.length ? reasons : ['below_threshold'],
        };
      }

      return {
        category: previousCategory,
        confidence: 'low',
        normalizedText,
        scores,
        matchedSignals: rawScores[previousCategory]?.matchedSignals || [],
        runnerUp,
        reason: ['inherited_from_previous'],
      };
    }

    return {
      category: allowUnknown ? 'unknown' : 'log',
      confidence,
      normalizedText,
      scores,
      matchedSignals: winner?.matchedSignals || [],
      runnerUp,
      reason: reasons.length ? reasons : ['below_threshold'],
    };
  }

  return {
    category: winner.category,
    confidence,
    normalizedText,
    scores,
    matchedSignals: winner.matchedSignals,
    runnerUp,
    reason: reasons,
  };
};

const DOMAIN_BOUNDARY_ALLOWED_PAIRS = new Set([
  'behavior|food',
  'food|behavior',
  'food|sleep',
  'sleep|food',
  'food|medication',
  'medication|food',
]);

const DOMAIN_BOUNDARY_MIN_WORDS = 2;

const DOMAIN_BOUNDARY_EVENT_PATTERNS = {
  behavior: [
    /\b(?:became|got|was|seemed|appeared|looked|felt)\s+(?:upset|frustrat(?:ed|ing)|cranky|irritable|angry|anxious|sad|mad)\b/i,
    /\b(?:yell(?:ed|ing)?|scream(?:ed|ing)?|push(?:ed|ing)?|hit(?:ting)?|slap(?:ped|ping)?|kick(?:ed|ing)?|bite(?:n|ing)?|throw(?:ing|n)?|cry(?:ing|cried)|needed redirection|needed support|needed a break|refused to participate|refused to join|did not want to participate|could not participate|calm(?:ed)? down|settled|support)\b/i,
  ],
  food: [
    /\b(?:ate|drank|eating|drinking|finished|refused|only ate|few bites|a few bites|most of|had\s+(?:a\s+few\s+bites|few\s+bites|some(?:\s+of)?(?:\s+her|\s+his|\s+their)?\s+(?:lunch|meal|snack|breakfast|dinner|food)|half(?:\s+of)?(?:\s+her|\s+his|\s+their)?\s+(?:lunch|meal|snack|breakfast|dinner|food)|breakfast|lunch|dinner|snack|meal)|did not want|didn't want|would not eat|wouldn't eat)\b/i,
  ],
  medication: [
    CARE_MEDICATION_DOMAIN_BOUNDARY_PATTERN,
  ],
  sleep: [
    /\b(?:fell asleep|asleep|slept|sleeping|woke up|woke|nap|napped|bedtime|went to bed|went to sleep|rest time)\b/i,
  ],
  activity: [
    /\b(?:participated|joined|worked|working with|played|playing|therapy|class|recess|music therapy|art class|group work|project|exercise|focused|engaged|followed directions)\b/i,
  ],
  mood: [
    /\b(?:happy|affectionate|tired|anxious|worried|excited|calm|calmer|frustrated|upset|sad|content|nervous|laugh(?:ed|ing)?|smiled|giggl(?:ed|ing)|overstimulated|cranky)\b/i,
  ],
  milestone: [
    /\b(?:progress|improv(?:ed|ement)|success(?:ful)?|independent(?:ly)?|proud|accomplishment|great job|did well|better than|nice improvement)\b/i,
  ],
};

const getDomainBoundaryWords = (text = '') => String(text || '')
  .trim()
  .split(/\s+/)
  .filter(Boolean);

const hasDomainBoundaryEventAnchor = (text = '', category = '') => {
  const normalizedText = String(text || '').trim();
  if (!normalizedText || !category) {
    return false;
  }

  const patterns = DOMAIN_BOUNDARY_EVENT_PATTERNS[category] || [];
  return patterns.some((pattern) => pattern.test(normalizedText));
};

const hasStrongDomainBoundarySignal = (text = '', category = '') => {
  if (!text || !category || category === 'unknown' || category === 'log') {
    return false;
  }

  const analysis = analyzeCategory(text, { allowUnknown: true });
  const analyzedCategory = String(analysis.category || '').trim().toLowerCase();
  if (analyzedCategory !== category) {
    return false;
  }

  const categoryScore = analysis.scores?.[category]?.score || 0;
  const threshold = CATEGORY_THRESHOLDS[category] || Number.MAX_SAFE_INTEGER;
  if (categoryScore < threshold) {
    return false;
  }

  return hasDomainBoundaryEventAnchor(text, category);
};

const isAllowedDomainBoundaryPair = (leftCategory = '', rightCategory = '') => {
  const pairKey = `${leftCategory}|${rightCategory}`;
  return DOMAIN_BOUNDARY_ALLOWED_PAIRS.has(pairKey);
};

const findDomainBoundarySplit = (sentence = '') => {
  const rawSentence = String(sentence || '').trim();
  if (!rawSentence) {
    return null;
  }

  const splitPattern = /\b(?:and(?:\s+later)?|and(?:\s+then)?)\b/ig;
  let match = splitPattern.exec(rawSentence);

  while (match) {
    const left = rawSentence.slice(0, match.index).replace(/[,\s]+$/u, '').trim();
    const right = rawSentence.slice(match.index + match[0].length).replace(/^[,\s]+/u, '').trim();

    const leftWordCount = getDomainBoundaryWords(left).length;
    const rightWordCount = getDomainBoundaryWords(right).length;

    if (leftWordCount >= DOMAIN_BOUNDARY_MIN_WORDS && rightWordCount >= DOMAIN_BOUNDARY_MIN_WORDS) {
      const leftAnalysis = analyzeCategory(left, { allowUnknown: true });
      const rightAnalysis = analyzeCategory(right, { allowUnknown: true });
      const leftCategory = String(leftAnalysis.category || '').trim().toLowerCase();
      const rightCategory = String(rightAnalysis.category || '').trim().toLowerCase();

      if (
        isAllowedDomainBoundaryPair(leftCategory, rightCategory)
        && hasStrongDomainBoundarySignal(left, leftCategory)
        && hasStrongDomainBoundarySignal(right, rightCategory)
      ) {
        const hasBehaviorSupportLanguage = /\b(?:headphones?|sensory break|quiet area|calm(?:ed)? down|settled|support|prompt(?:ed|ing)?|redirect(?:ed|ion)?|regulat(?:ed|ion)?)\b/i.test(`${left} ${right}`);
        const hasMedicationSleepPair = (
          (leftCategory === 'medication' && rightCategory === 'sleep')
          || (leftCategory === 'sleep' && rightCategory === 'medication')
        );
        const hasSleepOutcomeLanguage = /\b(?:fell asleep|asleep|slept|sleeping|woke up|bedtime|went to bed|went to sleep)\b/i.test(`${left} ${right}`);

        if (!hasBehaviorSupportLanguage && !(hasMedicationSleepPair && hasSleepOutcomeLanguage)) {
          return { left, right };
        }
      }
    }

    match = splitPattern.exec(rawSentence);
  }

  return null;
};

const findFoodBehaviorBoundarySplit = (sentence = '') => {
  const rawSentence = String(sentence || '').trim();
  if (!rawSentence) {
    return null;
  }

  const commaPattern = /,\s+/g;
  let match = commaPattern.exec(rawSentence);

  while (match) {
    const left = rawSentence.slice(0, match.index).replace(/[,\s]+$/u, '').trim();
    const right = rawSentence.slice(match.index + match[0].length).replace(/^[,\s]+/u, '').trim();

    const leftWordCount = getDomainBoundaryWords(left).length;
    const rightWordCount = getDomainBoundaryWords(right).length;

    if (leftWordCount >= DOMAIN_BOUNDARY_MIN_WORDS && rightWordCount >= DOMAIN_BOUNDARY_MIN_WORDS) {
      const leftAnalysis = analyzeCategory(left, { allowUnknown: true });
      const rightAnalysis = analyzeCategory(right, { allowUnknown: true });
      const leftCategory = String(leftAnalysis.category || '').trim().toLowerCase();
      const rightCategory = String(rightAnalysis.category || '').trim().toLowerCase();

      const foodToBehaviorOrMood = leftCategory === 'food' && ['behavior', 'mood'].includes(rightCategory);
      const behaviorToFood = ['behavior', 'mood'].includes(leftCategory) && rightCategory === 'food';

      if (
        (foodToBehaviorOrMood || behaviorToFood)
        && hasStrongDomainBoundarySignal(left, leftCategory)
        && hasStrongDomainBoundarySignal(right, rightCategory)
      ) {
        const rightHasCalmingSupport = /\b(?:calm(?:ed)? down|settled|headphones?|sensory break|support|regulated|quiet area)\b/i.test(right);
        const rightHasBehaviorEscalation = /\b(?:upset|frustrat(?:ed|ing)|cranky|irritable|angry|anxious|sad|mad|yell(?:ed|ing)?|cry(?:ing|ied)|refused to participate|refused to join|needed redirection|needed support)\b/i.test(right);
        const leftHasCalmingSupport = /\b(?:calm(?:ed)? down|settled|headphones?|sensory break|support|regulated|quiet area)\b/i.test(left);

        if (
          (foodToBehaviorOrMood && (rightHasBehaviorEscalation || rightHasCalmingSupport) && !leftHasCalmingSupport)
          || (behaviorToFood && rightCategory === 'food')
        ) {
          return { left, right };
        }
      }
    }

    match = commaPattern.exec(rawSentence);
  }

  return null;
};

const splitDomainBoundarySentence = (sentence = '', {
  enableFoodBehaviorBoundarySplit = false,
} = {}) => {
  const rawSentence = String(sentence || '').trim();
  if (!rawSentence) {
    return [];
  }

  const boundary = findDomainBoundarySplit(rawSentence)
    || (enableFoodBehaviorBoundarySplit ? findFoodBehaviorBoundarySplit(rawSentence) : null);
  if (!boundary) {
    return [rawSentence];
  }

  return [
    ...splitDomainBoundarySentence(boundary.left, { enableFoodBehaviorBoundarySplit }),
    ...splitDomainBoundarySentence(boundary.right, { enableFoodBehaviorBoundarySplit }),
  ];
};

export const classifyCareLogCategory = (inputText = '', options = {}) => {
  const analysis = analyzeCategory(inputText, options);
  return analysis.category;
};

export const hasCategoryEvidence = (category = '', inputText = '') => {
  const analysis = analyzeCategory(inputText, { allowUnknown: true });
  return Boolean(analysis.scores?.[category]?.score >= (CATEGORY_THRESHOLDS[category] || Number.MAX_SAFE_INTEGER));
};

export const shouldInheritCategory = (inputText = '', previousCategory = '') => {
  const analysis = analyzeCategory(inputText, { previousCategory, allowUnknown: true });
  return analysis.category === previousCategory && analysis.reason?.includes('inherited_from_previous');
};

export const debugCategoryDecision = (label, inputText, analysis) => {
  if (!isDebugFlagEnabled('debugCategoryClassifier')) {
    return;
  }

  // eslint-disable-next-line no-console
  console.log(`[CategoryClassifier] ${label}`, {
    inputText,
    normalizedText: analysis?.normalizedText || normalizeCategoryText(inputText),
    scores: analysis?.scores || {},
    matchedSignals: analysis?.matchedSignals || [],
    category: analysis?.category || 'unknown',
    runnerUp: analysis?.runnerUp?.category || null,
    reason: analysis?.reason || [],
    confidence: analysis?.confidence || 'none',
  });
};
